import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export type Density = "comfortable" | "compact";

const STORAGE_KEY = "smplify-density";

interface DensityContextValue {
  density: Density;
  setDensity: (d: Density) => void;
  toggle: () => void;
}

const DensityContext = createContext<DensityContextValue | undefined>(undefined);

function readStored(): Density {
  if (typeof window === "undefined") return "comfortable";
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "compact" ? "compact" : "comfortable";
}

export function DensityProvider({ children }: { children: ReactNode }) {
  const [density, setDensityState] = useState<Density>(() => readStored());

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.density = density;
    try { window.localStorage.setItem(STORAGE_KEY, density); } catch { /* noop */ }
  }, [density]);

  const value = useMemo<DensityContextValue>(
    () => ({
      density,
      setDensity: setDensityState,
      toggle: () => setDensityState((d) => (d === "compact" ? "comfortable" : "compact")),
    }),
    [density],
  );

  return <DensityContext.Provider value={value}>{children}</DensityContext.Provider>;
}

export function useDensity() {
  const ctx = useContext(DensityContext);
  if (!ctx) throw new Error("useDensity must be used within DensityProvider");
  return ctx;
}
